import { isRealTimestamp } from '~/utils/date';

import type { StatusTone } from '../constants/status-tones';
import type { ProductTimelineEntry } from '../types/meta-delivery.types';

/**
 * What happened to the product in one delivery job, folded from the Meta
 * ingest status, our validation gate and the coarse outcome. This is the
 * headline a support engineer reads first — it must say the *effect*
 * ("held back", "rejected by Meta"), not echo the raw status.
 */
export type TimelineVerdictKind =
  | 'validation_failed'
  | 'withheld'
  | 'rejected'
  | 'transport_failed'
  | 'queued'
  | 'uploaded'
  | 'confirm_pending'
  | 'persisted'
  | 'awaiting_ownership'
  | 'delivered'
  | 'skipped_unchanged'
  | 'excluded'
  | 'failed'
  | 'none';

/** Ingest statuses that mean the item reached Meta (whatever Meta then said). */
const REACHED_META = new Set([
  'UPLOADED',
  'PERSISTED',
  'CONFIRM_PENDING',
  'ROW_REJECTED',
  'TRANSPORT_FAILED',
]);

/** `true` for a zero / epoch placeholder timestamp masquerading as a real time. */
export function isEpochSentinel(iso: string | null | undefined): boolean {
  return !isRealTimestamp(iso);
}

/** `true` when this job actually pushed the item to Meta. */
export function wasSentToMeta(entry: ProductTimelineEntry): boolean {
  return REACHED_META.has(entry.ingestStatus) || entry.outcome === 'DELIVERED';
}

/** Fold the entry's statuses into a single verdict kind. Order matters: our own
 *  gate beats Meta's status, and Meta's per-item status beats the coarse outcome. */
export function deriveVerdictKind(entry: ProductTimelineEntry): TimelineVerdictKind {
  if (entry.validationStatus === 'FAILED' || entry.ingestReason === 'VALIDATION_FAILED') {
    return 'validation_failed';
  }
  switch (entry.ingestStatus) {
    case 'WITHHELD':
      return entry.ingestReason === 'EXCLUDED_FROM_FEED' ? 'excluded' : 'withheld';
    case 'ROW_REJECTED':
      return 'rejected';
    case 'TRANSPORT_FAILED':
      return 'transport_failed';
    case 'QUEUED':
      return 'queued';
    case 'UPLOADED':
      return 'uploaded';
    case 'CONFIRM_PENDING':
      return 'confirm_pending';
    case 'PERSISTED':
      return 'persisted';
    case 'AWAITING_OWNERSHIP':
      return 'awaiting_ownership';
    default:
      break;
  }
  switch (entry.outcome) {
    case 'DELIVERED':
      return 'delivered';
    case 'SKIPPED_UNCHANGED':
      return 'skipped_unchanged';
    case 'EXCLUDED':
      return 'excluded';
    case 'VALIDATION_FAILED':
      return 'validation_failed';
    case 'FAILED':
      return 'failed';
    default:
      return 'none';
  }
}

/** Verdict → chip / band tone. Only a Meta confirmation earns success. */
export function verdictTone(kind: TimelineVerdictKind): StatusTone {
  switch (kind) {
    case 'persisted':
    case 'delivered':
      return 'success';
    case 'uploaded':
    case 'confirm_pending':
    case 'queued':
      return 'info';
    case 'withheld':
    case 'awaiting_ownership':
    case 'transport_failed':
    case 'skipped_unchanged':
    case 'excluded':
      return 'warning';
    case 'validation_failed':
    case 'rejected':
    case 'failed':
      return 'error';
    default:
      return 'muted';
  }
}
